// API Response Types
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  errors?: Record<string, string[]>;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

// Authentication Types (Better Auth)
export interface LoginRequest {
  email: string;
  password: string;
}

export interface User {
  id: string;
  email: string;
  name: string;
  image?: string;
  role: UserRole;
  emailVerified: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Session {
  id: string;
  userId: string;
  expiresAt: string;
}

export interface AuthSession {
  user: User;
  session: Session;
}

export type UserRole = 'admin' | 'editor' | 'viewer';

// Content Management Types
export interface Content {
  id: string;
  filename: string;
  original_filename: string;
  title?: string;
  artist?: string;
  album?: string;
  genre?: string;
  duration?: number;
  file_size: number;
  mime_type: string;
  file_hash: string;
  r2_key: string;
  workflow_state: ContentWorkflowState;
  content_type: ContentType;
  thumbnail_r2_key?: string;
  upload_date: string;
  processed_date?: string;
  approved_date?: string;
  created_at: string;
  updated_at: string;
  uploaded_by: User;
  metadata?: ContentMetadata;
  tags: ContentTag[];
  play_count: number;
  // Extended metadata fields
  album_artist?: string;
  composer?: string;
  grouping?: string;
  year?: number;
  track_number?: number;
  track_total?: number;
  disc_number?: number;
  disc_total?: number;
  bpm?: number;
  rating?: number;
  comments?: string;
  lyrics?: string;
  is_compilation?: boolean;
  is_explicit?: boolean;
  volume_adjustment?: number;
  skip_when_shuffling?: boolean;
  sort_title?: string;
  sort_artist?: string;
  sort_album?: string;
  sort_album_artist?: string;
  sort_composer?: string;
  artwork_url?: string;
  artwork_r2_key?: string;
  copyright?: string;
}

export enum ContentWorkflowState {
  UPLOADED = 'uploaded',
  PROCESSING = 'processing',
  PROCESSED = 'processed',
  REVIEW = 'review',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  ARCHIVED = 'archived',
}

export enum ContentType {
  AUDIO = 'audio',
  VIDEO = 'video',
  IMAGE = 'image',
  DOCUMENT = 'document',
}

export interface ContentMetadata {
  // Audio properties
  bitrate?: number;
  sample_rate?: number;
  channels?: number;
  codec?: string;
  format?: string;
  quality_score?: number;
  loudness?: number;
  peak_level?: number;
  spectral_centroid?: number;
  tempo?: number;
  key?: string;
  energy?: number;
  danceability?: number;
  // Video properties (for music videos)
  video_codec?: string;
  video_bitrate?: number;
  resolution_width?: number;
  resolution_height?: number;
  frame_rate?: number;
  aspect_ratio?: string;
  has_audio?: boolean;
}

export interface ContentTag {
  id: string;
  name: string;
  color?: string;
}

export interface ContentVersion {
  id: string;
  content_id: string;
  version_number: number;
  filename: string;
  file_size: number;
  created_at: string;
  created_by: User;
}

// Upload Types
export interface UploadRequest {
  file: File;
  title?: string;
  artist?: string;
  album?: string;
  genre?: string;
  tags?: string[];
  category?: string;
  is_ai_generated?: boolean;
  register_to_bucket?: boolean;
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
  speed?: number;
  timeRemaining?: number;
}

export interface UploadResponse {
  content_id: string;
  filename: string;
  file_size: number;
  content_type: 'audio' | 'video';
  status: string;
  message: string;
}

// Analytics Types
export interface AnalyticsStats {
  total_content: number;
  total_plays: number;
  active_sessions: number;
  processing_queue: number;
  storage_used: number;
  storage_limit: number;
  upload_count_today: number;
  success_rate: number;
}

export interface PlayAnalytics {
  content_id: string;
  title: string;
  artist?: string;
  plays: number;
  duration: string;
  category: string;
  performance_score: number;
}

export interface UploadAnalytics {
  date: string;
  uploads: number;
  successful: number;
  failed: number;
  total_size: number;
}

export interface SystemHealth {
  api_status: 'healthy' | 'degraded' | 'down';
  database_status: 'healthy' | 'degraded' | 'down';
  storage_status: 'healthy' | 'degraded' | 'down';
  queue_status: 'healthy' | 'degraded' | 'down';
  response_time: number;
  cpu_usage: number;
  memory_usage: number;
  disk_usage: number;
  uptime: number;
}

// Settings Types
export interface Settings {
  auto_process: boolean;
  duplicate_detection: boolean;
  quality_check: boolean;
  notifications: boolean;
  email_alerts: boolean;
  storage_limit: number;
  retention_days: number;
  max_file_size: number;
  session_timeout: number;
  max_login_attempts: number;
  require_2fa: boolean;
  encrypt_at_rest: boolean;
  audit_logging: boolean;
  anonymous_analytics: boolean;
}

export interface ApiKey {
  id: string;
  name: string;
  key: string;
  created_at: string;
  last_used_at?: string;
  expires_at?: string;
  is_active: boolean;
  permissions: string[];
}

// Search and Filter Types
export interface ContentFilters {
  search?: string;
  workflow_state?: ContentWorkflowState[];
  content_type?: ContentType[];
  format?: string; // mp3, wav, flac, mp4, mpeg
  uploaded_by?: string[];
  date_from?: string;
  date_to?: string;
  tags?: string[];
  tag_ids?: string[]; // Filter by tag UUIDs
  file_size_min?: number;
  file_size_max?: number;
  duration_min?: number;
  duration_max?: number;
}

export interface SortOptions {
  field: string;
  direction: 'asc' | 'desc';
}

export interface ListContentRequest {
  page?: number;
  size?: number;
  filters?: ContentFilters;
  sort?: SortOptions;
}

// Error Types
export interface ApiError {
  message: string;
  code: string;
  details?: Record<string, any>;
}

export interface ValidationError {
  field: string;
  message: string;
}

// Recent Upload Types (for Dashboard)
export interface RecentUpload {
  id: string;
  name: string;
  size: string;
  status: 'Queued' | 'Processing' | 'Completed' | 'Failed' | 'Unknown';
  progress: number;
  time: string;
  contentType: 'audio' | 'video';
  thumbnailKey?: string | null;
}

// Activity Types
export interface RecentActivity {
  id: string;
  type: 'upload' | 'approval' | 'rejection' | 'deletion' | 'user_action';
  description: string;
  content_id?: string;
  user_id: string;
  user_name: string;
  timestamp: string;
  status: 'success' | 'error' | 'info' | 'warning';
  metadata?: Record<string, any>;
}

// Fleet Management Types
export interface Organization {
  id: string;
  name: string;
  slug: string;
  contact_email?: string;
  contact_phone?: string;
  logo_url?: string;
  settings?: Record<string, any>;
  site_count?: number;
  device_count?: number;
  online_count?: number;
  created_at: string;
  updated_at: string;
}

export interface OrganizationMember {
  id: string;
  user_id: string;
  user_name: string;
  user_email: string;
  role: 'admin' | 'editor' | 'viewer';
  created_at: string;
}

export interface Site {
  id: string;
  organization_id: string;
  organization_name?: string;
  name: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
  latitude?: number;
  longitude?: number;
  timezone: string;
  settings?: Record<string, any>;
  device_count?: number;
  online_count?: number;
  offline_count?: number;
  created_at: string;
  updated_at: string;
}

export interface Device {
  id: string;
  organization_id: string;
  organization_name?: string;
  site_id?: string;
  site_name?: string;
  site_city?: string;
  serial_number: string;
  name: string;
  model?: string;
  firmware_version?: string;
  provisioning_token?: string;
  provisioned_at?: string;
  status: 'online' | 'offline' | 'error' | 'provisioning';
  last_heartbeat?: string;
  last_ip_address?: string;
  heartbeat_interval: number;
  settings?: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface DeviceHeartbeat {
  id: string;
  device_id: string;
  timestamp: string;
  status: string;
  ip_address?: string;
  cpu_usage?: number;
  memory_usage?: number;
  disk_usage?: number;
  temperature?: number;
  current_content_id?: string;
  uptime_seconds?: number;
  metadata?: Record<string, any>;
}

export interface DeviceEvent {
  id: string;
  device_id: string;
  device_name?: string;
  serial_number?: string;
  organization_name?: string;
  site_name?: string;
  event_type: string;
  severity: 'info' | 'warning' | 'error' | 'critical';
  message?: string;
  metadata?: Record<string, any>;
  created_at: string;
}

export interface AlertConfig {
  id: string;
  organization_id: string;
  organization_name?: string;
  name: string;
  enabled: boolean;
  event_types: string[];
  severity_threshold: string;
  cooldown_minutes: number;
  channel_type: 'slack' | 'google_chat';
  webhook_url: string;
  created_at: string;
  updated_at: string;
}

export interface AlertHistory {
  id: string;
  alert_config_id: string;
  config_name?: string;
  device_id?: string;
  device_name?: string;
  serial_number?: string;
  organization_name?: string;
  event_id?: string;
  status: 'sent' | 'failed' | 'suppressed';
  response_code?: number;
  created_at: string;
}

export interface FleetOverview {
  organizations: number;
  sites: number;
  devices: {
    total: number;
    online: number;
    offline: number;
    error: number;
    provisioning: number;
  };
  alerts_24h: number;
}

export interface FleetMapData {
  sites: Array<{
    id: string;
    name: string;
    city?: string;
    latitude: number;
    longitude: number;
    organization_id: string;
    organization_name: string;
    device_count: number;
    online_count: number;
    offline_count: number;
  }>;
  devices: Array<{
    id: string;
    name: string;
    serial_number: string;
    status: 'online' | 'offline' | 'error' | 'provisioning';
    last_heartbeat?: string;
    organization_id: string;
    organization_name: string;
    site_id?: string;
    site_name?: string;
    latitude?: number;
    longitude?: number;
  }>;
}

// User Management Types
export type UserStatus = 'pending' | 'active' | 'disabled';

export interface ManagedUser {
  id: string;
  email: string;
  name: string;
  phone?: string | null;
  role: UserRole;
  status: UserStatus;
  global_role?: string | null;
  emailVerified: boolean;
  image?: string | null;
  banned: boolean;
  banReason?: string | null;
  invited_by?: string | null;
  last_login_at?: string | null;
  createdAt: string;
  updatedAt: string;
  organizations?: Array<{
    id: string;
    name: string;
    role: string;
  }>;
}

export interface UserInvite {
  id: string;
  email: string;
  name?: string;
  organization_id: string;
  organization_name?: string;
  role: string;
  inviter_name?: string;
  inviter_email?: string;
  token: string;
  message?: string;
  expires_at: string;
  accepted_at?: string;
  revoked_at?: string;
  created_at: string;
}

export interface UserSession {
  id: string;
  ip_address: string;
  user_agent: string;
  created_at: string;
  expires_at: string;
  is_current: boolean;
}

export interface AuditEntry {
  id: string;
  user_id?: string;
  user_email: string;
  actor_name?: string;
  action: string;
  resource_type?: string;
  resource_id?: string;
  resource_name?: string;
  organization_id?: string;
  organization_name?: string;
  details: Record<string, any>;
  changes: Record<string, { old: any; new: any }>;
  ip_address?: string;
  user_agent?: string;
  request_id?: string;
  created_at: string;
}

export interface InviteUserRequest {
  email: string;
  name?: string;
  organization_id: string;
  role: 'admin' | 'editor' | 'viewer';
  resource_assignments?: Array<{
    resource_type: string;
    resource_id: string;
    role: string;
  }>;
  message?: string;
}

export interface InviteUserResponse {
  user: ManagedUser;
  invite?: UserInvite;
  invite_url?: string;
  added_to_org?: boolean;
}

export interface InviteDetails {
  email: string;
  name?: string;
  organization_name: string;
  role: string;
  inviter_name?: string;
  expires_at: string;
  is_expired: boolean;
  message?: string;
}

export interface AcceptInviteRequest {
  password: string;
  name?: string;
}

export interface AuditStats {
  total: number;
  by_action: Array<{ action: string; count: number }>;
  by_day: Array<{ date: string; count: number }>;
  top_actors: Array<{ user_email: string; count: number }>;
  period_days: number;
}

// =============================================================================
// RBAC Types (Role-Based Access Control)
// =============================================================================

export type PermissionScope = 'global' | 'organization' | 'resource';
export type PermissionCategory = 'content' | 'playlist' | 'fleet' | 'scheduling' | 'admin' | 'analytics' | 'songforge' | 'general';
export type RoleScope = 'global' | 'organization' | 'resource';

export interface Permission {
  id: string;
  resource: string;
  action: string;
  name: string;
  description?: string;
  category: PermissionCategory;
  scope_global: boolean;
  scope_organization: boolean;
  scope_resource: boolean;
  is_system: boolean;
  created_at: string;
}

export interface PermissionGroup {
  category: PermissionCategory;
  label: string;
  permissions: Permission[];
}

export interface Role {
  id: string;
  name: string;
  slug: string;
  description?: string;
  scope: RoleScope;
  organization_id?: string;
  organization_name?: string;
  parent_role_id?: string;
  parent_role_name?: string;
  priority: number;
  is_system: boolean;
  is_default: boolean;
  permission_count?: number;
  user_count?: number;
  created_at: string;
  updated_at: string;
}

export interface RolePermission {
  id: string;
  role_id: string;
  permission_id: string;
  granted: boolean;
  conditions: PermissionConditions;
  created_at: string;
  permission?: Permission;
  source_role_id?: string;
  source_role_name?: string;
  inherited?: boolean;
}

export interface PermissionConditions {
  own_only?: boolean;
  max_count?: number;
  time_restricted?: {
    start_hour: number;
    end_hour: number;
    days: number[];
  };
  [key: string]: any;
}

export interface RoleWithPermissions extends Role {
  permissions: RolePermission[];
  inherited_permissions: RolePermission[];
}

export interface UserRoleAssignment {
  id: string;
  user_id: string;
  role_id: string;
  organization_id?: string;
  resource_type?: string;
  resource_id?: string;
  assigned_by?: string;
  expires_at?: string;
  created_at: string;
  role?: Role;
  user_name?: string;
  user_email?: string;
  organization_name?: string;
  resource_name?: string;
  assigned_by_name?: string;
}

export interface CreateRoleRequest {
  name: string;
  slug?: string;
  description?: string;
  scope: RoleScope;
  organization_id?: string;
  parent_role_id?: string;
  priority?: number;
  permissions?: string[];
}

export interface UpdateRoleRequest {
  name?: string;
  description?: string;
  parent_role_id?: string | null;
  priority?: number;
}

export interface AssignRoleRequest {
  role_id: string;
  organization_id?: string;
  resource_type?: string;
  resource_id?: string;
  expires_at?: string;
}

export interface EffectivePermission {
  resource: string;
  action: string;
  granted: boolean;
  conditions: PermissionConditions;
  source_role: string;
  source_role_id: string;
}

export const SYSTEM_ROLES = {
  GLOBAL_ADMIN: 'role_global_admin',
  ORG_ADMIN: 'role_org_admin',
  EDITOR: 'role_editor',
  VIEWER: 'role_viewer',
} as const;

export const CATEGORY_LABELS: Record<PermissionCategory, string> = {
  content: 'Content Management',
  playlist: 'Playlists',
  fleet: 'Fleet & Devices',
  scheduling: 'Scheduling',
  admin: 'Administration',
  analytics: 'Analytics',
  songforge: 'SongForge AI',
  general: 'General',
};

export const SCOPE_LABELS: Record<RoleScope, string> = {
  global: 'Global (All Organizations)',
  organization: 'Organization',
  resource: 'Resource-Specific',
};

// =============================================================================
// Scheduling Types
// =============================================================================

export interface Zone {
  id: string;
  site_id: string;
  site_name?: string;
  organization_name?: string;
  name: string;
  description?: string;
  color: string;
  default_volume: number;
  crossfade_duration: number;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Segment {
  id: string;
  organization_id: string;
  organization_name?: string;
  name: string;
  description?: string;
  color: string;
  target_duration_minutes?: number;
  target_track_count?: number;
  bpm_min?: number;
  bpm_max?: number;
  energy_level?: number;
  mood_tags?: string[];
  texture?: 'acoustic' | 'electronic' | 'mixed';
  vocal_preference?: 'any' | 'vocal' | 'instrumental' | 'mixed';
  genre_filter?: string[];
  explicit_allowed: boolean;
  internal_shuffle?: boolean;
  crossfade_seconds?: number;
  track_count?: number;
  actual_track_count?: number;
  actual_duration_seconds?: number;
  avg_bpm?: number;
  avg_energy?: number;
  created_by?: string;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
  tracks?: SegmentItem[];
}

export interface SegmentItem {
  id: string;
  segment_id: string;
  content_id: string;
  position: number;
  transition_type: 'crossfade' | 'cut' | 'fade';
  added_at: string;
  // Content fields
  title?: string;
  artist?: string;
  duration?: number;
  bpm?: number;
  energy_level?: number;
  mood_tags?: string;
}

export interface Sequence {
  id: string;
  organization_id: string;
  organization_name?: string;
  name: string;
  description?: string;
  color: string;
  total_duration_seconds: number;
  segment_count: number;
  loop_mode: boolean;
  created_by?: string;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
  segments?: SequenceItem[];
}

export interface SequenceItem {
  id: string;
  sequence_id: string;
  segment_id: string;
  position: number;
  added_at: string;
  // Segment fields
  segment_name?: string;
  segment_color?: string;
  segment_duration?: number;
  energy_level?: number;
}

export interface Playlist {
  id: string;
  organization_id: string;
  organization_name?: string;
  name: string;
  description?: string;
  color: string;
  shuffle_mode: boolean;
  repeat_mode: 'none' | 'all' | 'one';
  crossfade_seconds: number;
  explicit_filter: boolean;
  artwork_url?: string;
  is_active: boolean;
  is_public: boolean;
  track_count: number;
  total_duration_seconds: number;
  created_by?: string;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
  tracks?: PlaylistItem[];
}

export interface PlaylistItem {
  id: string;
  playlist_id: string;
  content_id: string;
  position: number;
  added_by?: string;
  added_at: string;
  // Content fields
  title?: string;
  artist?: string;
  album?: string;
  duration?: number;
  bpm?: number;
  energy_level?: number;
  mood_tags?: string;
}

export interface Channel {
  id: string;
  organization_id: string;
  organization_name?: string;
  name: string;
  description?: string;
  color: string;
  stream_url: string;
  backup_stream_url?: string;
  stream_format: 'mp3' | 'aac' | 'ogg' | 'flac';
  bitrate: number;
  is_active: boolean;
  metadata_url?: string;
  logo_url?: string;
  created_at: string;
  updated_at: string;
}

export interface Schedule {
  id: string;
  zone_id: string;
  zone_name?: string;
  site_name?: string;
  organization_name?: string;
  name: string;
  description?: string;
  is_active: boolean;
  is_default: boolean;
  effective_from?: string;
  effective_to?: string;
  created_by?: string;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
  entries?: ScheduleEntry[];
}

export interface ScheduleEntry {
  id: string;
  schedule_id: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  content_type?: 'playlist' | 'sequence' | 'channel';
  priority: number;
  daypart_id?: string;
  daypart_name?: string;
  daypart_color?: string;
  playlist_id?: string;
  playlist_name?: string;
  playlist_color?: string;
  playlist_track_count?: number;
  sequence_id?: string;
  sequence_name?: string;
  sequence_color?: string;
  sequence_segment_count?: number;
  channel_id?: string;
  channel_name?: string;
  channel_color?: string;
  shuffle_override?: boolean;
  volume_override?: number;
  crossfade_override?: number;
  created_at: string;
  updated_at?: string;
}

export interface Daypart {
  id: string;
  name: string;
  description?: string;
  start_time: string;
  end_time: string;
  color: string;
  is_system: boolean;
  organization_id?: string;
}

export interface EnergyCurve {
  id: string;
  name: string;
  description?: string;
  curve_points: Array<{ time: number; energy: number }>;
  is_system: boolean;
  organization_id?: string;
  created_at: string;
}

export interface SchedulingDashboard {
  zones: Array<Zone & {
    now_playing?: ScheduleEntry | null;
    active_schedule_name?: string;
  }>;
  current_time: string;
  day_of_week: number;
  day_name: string;
}

export interface WeekSchedule {
  zone: Zone;
  schedule: Schedule | null;
  week: Record<number, ScheduleEntry[]>;
  day_names: string[];
}

export interface ScheduleGap {
  day_of_week: number;
  day_name: string;
  start_time: string;
  end_time: string;
  duration_minutes: number;
}

// =============================================================================
// AI Generation Types (SongForge)
// =============================================================================

export type AIGenerationStatus = 'pending' | 'prompt_ready' | 'generated' | 'uploaded' | 'failed';

export interface AIReferenceSong {
  id: string;
  organization_id: string;
  title: string;
  artist: string;
  reason?: string;
  musical_key?: string;
  bpm?: number;
  time_signature?: string;
  chord_progression?: string;
  genre?: string;
  mood?: string;
  energy_level?: number;
  analysis_prompt?: string;
  analysis_response?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface LinkedContent {
  id: string;
  queue_item_id: string;
  content_id: string;
  is_primary: number;
  variation_label?: string;
  notes?: string;
  created_at: string;
  // Joined from content table
  content_title?: string;
  content_artist?: string;
  duration?: number;
  r2_key?: string;
}

export interface AIGenerationQueueItem {
  id: string;
  organization_id: string;
  reference_song_id?: string;
  reference_title?: string;
  reference_artist?: string;
  reference_analysis?: string;
  theme?: string;
  target_genre?: string;
  target_mood?: string;
  target_energy?: number;
  style_prompt?: string;
  style_tags?: string;
  lyrics?: string;
  status: AIGenerationStatus;
  generated_content_id?: string;
  quality_rating?: number;
  notes?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
  // Multi-track support
  linked_content?: LinkedContent[];
}

export interface AIPromptTemplate {
  id: string;
  organization_id?: string;
  name: string;
  description?: string;
  claude_prompt_template?: string;
  style_prompt_template?: string;
  genre?: string;
  mood?: string;
  is_active: boolean;
  is_system: boolean;
  usage_count: number;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateAIReferenceSongRequest {
  organization_id: string;
  title: string;
  artist: string;
  reason?: string;
  musical_key?: string;
  bpm?: number;
  time_signature?: string;
  chord_progression?: string;
  genre?: string;
  mood?: string;
  energy_level?: number;
  analysis_prompt?: string;
  analysis_response?: string;
}

export interface CreateAIGenerationQueueRequest {
  organization_id: string;
  reference_song_id?: string;
  theme?: string;
  target_genre?: string;
  target_mood?: string;
  target_energy?: number;
  style_prompt?: string;
  style_tags?: string;
  lyrics?: string;
  status?: AIGenerationStatus;
  notes?: string;
}

export interface CreateAIPromptTemplateRequest {
  organization_id?: string;
  name: string;
  description?: string;
  claude_prompt_template?: string;
  style_prompt_template?: string;
  genre?: string;
  mood?: string;
}

export interface AIGenerationCoverage {
  by_genre: Array<{ genre: string; count: number }>;
  by_mood: Array<{ mood: string; count: number }>;
  by_energy: Array<{ energy_level: number; count: number }>;
  genre_mood_matrix: Array<{ genre: string; mood: string; count: number }>;
}

export interface AIGenerationStats {
  by_status: Array<{ status: AIGenerationStatus; count: number }>;
  by_month: Array<{ month: string; count: number }>;
  by_rating: Array<{ quality_rating: number; count: number }>;
  reference_count: number;
  template_count: number;
  total_in_queue: number;
  success_rate: string;
}

export interface AIGenerationFilters {
  organization_id?: string;
  status?: AIGenerationStatus;
  target_genre?: string;
  target_mood?: string;
  search?: string;
}